/**
 * Payments hooks with mock fallback (same contract as the Phase 5.1 clients hooks).
 * Reads fall back to the mock store when anonymous or the API errors. Writes hit the
 * API when authenticated (and invalidate), otherwise mutate the mock store.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAYMENTS_QUERY_PREFIX, REPORTS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Payment, PaymentStatus } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { paymentsApi } from "./payments.api";
import { toMockPayment, toPayment, toPaymentWriteBody, type PaymentInput } from "./payments.adapter";
import { usePaymentsStore } from "./payments.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UsePaymentsResult {
  payments: Payment[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function usePayments(): UsePaymentsResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = usePaymentsStore((s) => s.active);

  const query = useQuery({
    queryKey: queryKeys.paymentsList(LIST_PARAMS),
    queryFn: () => paymentsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { payments: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { payments: query.data.data.map(toPayment), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { payments: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { payments: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

export function usePayment(paymentId: string): {
  payment: Payment | null;
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  error: unknown;
} {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = usePaymentsStore((s) => s.active);
  const fallback = mockActive.find((payment) => payment.id === paymentId) ?? null;

  const query = useQuery({
    queryKey: queryKeys.payment(paymentId),
    queryFn: () => paymentsApi.getById(paymentId),
    enabled: authed && !!paymentId,
  });

  if (!authed) return { payment: fallback, isLoading: false, isError: false, source: "mock", error: null };
  if (query.data) return { payment: toPayment(query.data), isLoading: false, isError: false, source: "api", error: null };
  if (query.isError) return { payment: fallback, isLoading: false, isError: true, source: fallback ? "mock" : "api", error: query.error };
  return { payment: null, isLoading: query.isLoading, isError: false, source: "api", error: null };
}

function useInvalidatePayments() {
  const qc = useQueryClient();
  return () => Promise.all([
    qc.invalidateQueries({ queryKey: PAYMENTS_QUERY_PREFIX }),
    qc.invalidateQueries({ queryKey: REPORTS_QUERY_PREFIX }),
  ]);
}

export function useCreatePayment() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = usePaymentsStore((s) => s.add);
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (input: PaymentInput) => {
      if (authed) return paymentsApi.create(toPaymentWriteBody(input));
      const mock = toMockPayment(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useUpdatePayment() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = usePaymentsStore((s) => s.update);
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: PaymentInput }) => {
      if (authed) return paymentsApi.update(id, toPaymentWriteBody(input));
      update(id, { ...input });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useUpdatePaymentStatus() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = usePaymentsStore((s) => s.update);
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PaymentStatus }) => {
      if (authed) return paymentsApi.update(id, { status });
      update(id, {
        status,
        receivedAt: status === "RECEIVED" ? new Date().toISOString().slice(0, 10) : undefined,
      });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useArchivePayment() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = usePaymentsStore((s) => s.archive);
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return paymentsApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestorePayment() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = usePaymentsStore((s) => s.restore);
  const invalidate = useInvalidatePayments();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return paymentsApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

const ARCHIVED_PARAMS = { archived: true, limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export function useArchivedPayments() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const query = useQuery({
    queryKey: queryKeys.paymentsList(ARCHIVED_PARAMS),
    queryFn: () => paymentsApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data) return { payments: query.data.data.map(toPayment), isLoading: false, isError: false, refetch: query.refetch };
  if (query.isError) return { payments: [] as Payment[], isLoading: false, isError: true, refetch: query.refetch };
  return { payments: [] as Payment[], isLoading: query.isLoading, isError: false, refetch: query.refetch };
}
